import {
  Box,
  Grid,
  Typography,
  IconButton,
  TextField,
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  Snackbar,
  Alert,
  styled
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteForever, Search, Receipt, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import { apiUrl } from '../../constants';

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  paddingTop: '2rem',
  paddingBottom: '2rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease-in-out',
  padding: '2rem',
  maxWidth: '500px',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    '& fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#667eea',
    fontWeight: '500',
  },
  '& input': {
    height: '40px',
    padding: '0px 14px !important',
    fontSize: '1rem',
  }
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  boxShadow: '0 4px 15px rgba(103, 126, 234, 0.4)',
  transition: 'all 0.3s ease',
  minWidth: '180px',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(103, 126, 234, 0.6)',
  },
  '&:disabled': {
    background: 'rgba(103, 126, 234, 0.3)',
    color: 'rgba(255, 255, 255, 0.7)',
  }
}));

const Label = styled(Typography)({
  marginBottom: '12px',
  fontWeight: '600',
  color: '#374151',
  fontSize: '1rem',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontWeight: '700',
  marginBottom: '2rem',
  textAlign: 'center',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
  position: 'absolute',
  top: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
}));

const CheckConsent = () => {
  const { token } = useSelector(state => state.userSlice);
  const [id, setId] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const fetchConsent = async () => {
    if (!id.trim()) {
      setToast({ open: true, message: 'Please enter a ticket ID', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.get(`${apiUrl}/admin/check-consent/${id}`, {
        headers: { token }
      });
      
      if (resp.data.ticket?.riskConsentImage?.url) {
        window.open(resp.data.ticket?.riskConsentImage?.url);
        setToast({ open: true, message: 'Consent form opened successfully!', severity: 'success' });
      } else {
        setToast({ open: true, message: "Consent form for this ticket doesn't exist", severity: 'error' });
      }
    } catch (error) {
      setToast({ open: true, message: 'Failed to fetch consent form', severity: 'error' });
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchConsent();
    }
  };

  return (
    <StyledContainer>
      <SectionTitle variant='h4'>
        <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
        Check Consent Form
      </SectionTitle>
      
      <Container maxWidth="sm">
        <Fade in={true} timeout={800}>
          <GlassCard>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Receipt sx={{ fontSize: '3rem', color: '#667eea', mb: 1 }} />
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: '600',
                        color: '#1f2937',
                        mb: 1,
                      }}
                    >
                      Consent Form Checker
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6b7280',
                        fontSize: '0.9rem',
                      }}
                    >
                      Enter the ticket ID to view the associated consent form
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Label>
                    <Receipt sx={{ fontSize: '1.2rem' }} />
                    Ticket ID
                  </Label>
                  <StyledTextField
                    fullWidth
                    value={id}
                    onChange={e => setId(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder='Enter ticket ID (e.g. TKT123456)'
                    InputProps={{
                      startAdornment: <Search sx={{ color: '#667eea', mr: 1 }} />,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <GradientButton
                    fullWidth
                    variant='contained'
                    size="large"
                    onClick={fetchConsent}
                    disabled={loading}
                    startIcon={loading ? null : <Search />}
                  >
                    {loading ? 'Fetching...' : 'Fetch Consent Form'}
                  </GradientButton>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      background: 'rgba(103, 126, 234, 0.1)',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid rgba(103, 126, 234, 0.2)',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#667eea',
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: '1rem' }} />
                      The consent form will open in a new tab if it exists
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>
        </Fade>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default CheckConsent;